import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { teacherStudentsApi } from '../../services/teacherStudentsApi';
import {
  AddStudentModal,
  Avatar,
  Breadcrumbs,
  ContextFields,
  GroupModal,
  LoadError,
  OverviewCard,
  StatusBadge,
  formatDate
} from './TeacherMyStudentsParts';

const FALLBACK_PATHS = [
  { id: 'educational-levels-vocabulary', title: "Educational Levels' Vocabulary", studentCount: 0, groupCount: 0 },
  { id: 'international-exam-vocabulary', title: 'International Exam Vocabulary', studentCount: 0, groupCount: 0 },
  { id: 'general-vocabulary', title: 'General Vocabulary', studentCount: 0, groupCount: 0 },
  { id: 'academic-vocabulary-esp', title: 'Academic Vocabulary (ESP)', studentCount: 0, groupCount: 0 },
  { id: 'dictation', title: 'Dictation', studentCount: 0, groupCount: 0 },
  { id: 'reading', title: 'Reading', studentCount: 0, groupCount: 0 }
];

const PATH_ICONS = {
  'educational-levels-vocabulary': 'school',
  'international-exam-vocabulary': 'assignment',
  'general-vocabulary': 'menu_book',
  'academic-vocabulary-esp': 'science',
  dictation: 'mic',
  reading: 'auto_stories'
};

function PageShell({ children }) {
  return <div className="teacher-main-content">{children}</div>;
}

function LoadingNote() {
  const { t } = useTranslation('teacher');
  return <p className="teacher-content-subtitle">{t('common.loading')}</p>;
}

function EmptyState({ title, hint, children }) {
  return (
    <div className="teacher-section">
      <div className="teacher-empty-state">
        <h3>{title}</h3>
        {hint && <p>{hint}</p>}
        {children}
      </div>
    </div>
  );
}

export function Landing({ go }) {
  const { t } = useTranslation('teacher');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    teacherStudentsApi.overview().then(setData).catch((err) => setError(err.message)).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <PageShell>
      <div className="teacher-content-header">
        <h1 className="teacher-content-title">{t('myStudents.title')}</h1>
        <p className="teacher-content-subtitle">{t('myStudents.subtitle')}</p>
      </div>
      {loading && <LoadingNote />}
      {error && <LoadError message={error} onRetry={load} />}
      {!loading && !error && data && (
        <>
          <div className="teacher-section">
            <div className="teacher-section-header">
              <div className="teacher-section-title">
                <i className="material-icons">route</i>
                {t('myStudents.learningPathStudents')}
              </div>
              <div className="teacher-header-actions">
                <button type="button" className="teacher-btn primary" onClick={() => go('/teacher-dashboard/my-students/learning-paths')}>
                  <i className="material-icons">people</i>
                  {t('myStudents.manageLearningPathStudents')}
                </button>
              </div>
            </div>
            <p className="teacher-section-desc">{t('myStudents.learningPathDesc')}</p>
            <div className="teacher-overview-grid">
              <OverviewCard title={t('myStudents.totalStudents')} value={data.learningPaths.totalStudents} icon="people" />
              <OverviewCard title={t('myStudents.totalGroups')} value={data.learningPaths.totalGroups} icon="groups" variant="success" />
              <OverviewCard title={t('myStudents.pathsInUse')} value={data.learningPaths.pathsInUse} icon="route" variant="accent" />
            </div>
          </div>
          <div className="teacher-section">
            <div className="teacher-section-header">
              <div className="teacher-section-title">
                <i className="material-icons">menu_book</i>
                {t('myStudents.courseStudents')}
              </div>
              <div className="teacher-header-actions">
                <button type="button" className="teacher-btn primary" onClick={() => go('/teacher-dashboard/my-students/courses')}>
                  <i className="material-icons">school</i>
                  {t('myStudents.manageCourseStudents')}
                </button>
              </div>
            </div>
            <p className="teacher-section-desc">{t('myStudents.courseDesc')}</p>
            <div className="teacher-overview-grid">
              <OverviewCard title={t('myStudents.totalStudents')} value={data.courses.totalStudents} icon="people" />
              <OverviewCard title={t('myStudents.totalCourses')} value={data.courses.totalCourses} icon="menu_book" variant="success" />
              <OverviewCard title={t('myStudents.totalGroups')} value={data.courses.totalGroups} icon="groups" variant="info" />
            </div>
          </div>
        </>
      )}
    </PageShell>
  );
}

export function PathList({ go }) {
  const { t } = useTranslation('teacher');
  const [paths, setPaths] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    teacherStudentsApi.learningPaths().then((d) => setPaths(d.paths || [])).catch((err) => setError(err.message)).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <PageShell>
      <Breadcrumbs items={[
        { label: t('myStudents.title'), onClick: () => go('/teacher-dashboard/my-students') },
        { label: t('myStudents.learningPaths') }
      ]} />
      <div className="teacher-content-header">
        <h1 className="teacher-content-title">{t('myStudents.learningPathStudents')}</h1>
        <p className="teacher-content-subtitle">{t('myStudents.pathListHint')}</p>
      </div>
      {loading && <LoadingNote />}
      {error && <LoadError message={error} onRetry={load} />}
      {!loading && !error && (
        <div className="teacher-section">
          <div className="teacher-actions-grid">
            {FALLBACK_PATHS.map((fallback) => {
              const path = paths.find((p) => p.id === fallback.id) || fallback;
              return (
                <button
                  type="button"
                  key={fallback.id}
                  className="teacher-action-card"
                  onClick={() => go(`/teacher-dashboard/my-students/learning-paths/${path.id}`)}
                >
                  <div className="action-icon">
                    <i className="material-icons">{PATH_ICONS[fallback.id] || 'school'}</i>
                  </div>
                  <div className="action-content">
                    <h4>{path.title}</h4>
                    <p>{path.studentCount || 0} {t('myStudents.students')} · {path.groupCount || 0} {t('myStudents.groups')}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </PageShell>
  );
}

export function PathManage({ go, pathId }) {
  const { t } = useTranslation('teacher');
  const [path, setPath] = useState(null);
  const [groups, setGroups] = useState([]);
  const [filters, setFilters] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showGroup, setShowGroup] = useState(false);
  const [showStudent, setShowStudent] = useState(false);
  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    teacherStudentsApi.pathDetail(pathId, filters)
      .then((d) => { setPath(d.path); setGroups(d.groups || []); })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [pathId, filters]);
  useEffect(() => { load(); }, [load]);
  const empty = !loading && !error && groups.length === 0;
  return (
    <PageShell>
      <Breadcrumbs items={[
        { label: t('myStudents.title'), onClick: () => go('/teacher-dashboard/my-students') },
        { label: t('myStudents.learningPaths'), onClick: () => go('/teacher-dashboard/my-students/learning-paths') },
        { label: path?.title || pathId }
      ]} />
      <div className="teacher-content-header">
        <h1 className="teacher-content-title">{path?.title || t('myStudents.learningPathStudents')}</h1>
        <p className="teacher-content-subtitle">{path ? `${path.studentCount} ${t('myStudents.students')} · ${path.groupCount} ${t('myStudents.groups')}` : ''}</p>
      </div>
      <div className="teacher-section">
        <div className="teacher-filters">
          <div className="teacher-filter-group">
            <ContextFields pathId={pathId} context={filters} setContext={setFilters} />
          </div>
          <div className="teacher-header-actions">
            <button type="button" className="teacher-btn secondary" onClick={() => setShowStudent(true)}>
              <i className="material-icons">person_add</i>
              {t('myStudents.addStudent')}
            </button>
            <button type="button" className="teacher-btn primary" onClick={() => setShowGroup(true)}>
              <i className="material-icons">group_add</i>
              {t('myStudents.createGroup')}
            </button>
          </div>
        </div>
        {loading && <LoadingNote />}
        {error && <LoadError message={error} onRetry={load} />}
        {empty && (
          <div className="teacher-empty-state">
            <h3>{t('myStudents.noStudentsOrGroups')}</h3>
            <p>{t('myStudents.emptyHint')}</p>
            <div className="teacher-header-actions">
              <button type="button" className="teacher-btn secondary" onClick={() => setShowStudent(true)}>
                <i className="material-icons">person_add</i>
                {t('myStudents.addStudent')}
              </button>
              <button type="button" className="teacher-btn primary" onClick={() => setShowGroup(true)}>
                <i className="material-icons">group_add</i>
                {t('myStudents.createGroup')}
              </button>
            </div>
          </div>
        )}
        {!loading && !error && groups.length > 0 && (
          <div className="teacher-actions-grid">
            {groups.map((g) => (
              <button
                type="button"
                key={g.id}
                className="teacher-action-card"
                onClick={() => go(`/teacher-dashboard/my-students/learning-paths/${pathId}/groups/${g.id}`)}
              >
                <div className="action-icon">
                  <i className="material-icons">groups</i>
                </div>
                <div className="action-content">
                  <h4>{g.name}</h4>
                  <p>
                    {g.studentCount} {t('myStudents.students')}
                    {g.contextLabel ? ` · ${g.contextLabel}` : ''}
                  </p>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
      {showGroup && <GroupModal pathId={pathId} onClose={() => setShowGroup(false)} onCreated={() => { setShowGroup(false); load(); }} />}
      {showStudent && <AddStudentModal pathId={pathId} groups={groups} onClose={() => setShowStudent(false)} onAdded={() => { setShowStudent(false); load(); }} />}
    </PageShell>
  );
}

export function GroupPage({ go, groupId, pathId, courseId }) {
  const { t } = useTranslation('teacher');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [showStudent, setShowStudent] = useState(false);
  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    teacherStudentsApi.group(groupId, { search, status }).then(setData).catch((err) => setError(err.message)).finally(() => setLoading(false));
  }, [groupId, search, status]);
  useEffect(() => { load(); }, [load]);
  const group = data?.group;
  const crumbs = pathId ? [
    { label: t('myStudents.title'), onClick: () => go('/teacher-dashboard/my-students') },
    { label: t('myStudents.learningPaths'), onClick: () => go('/teacher-dashboard/my-students/learning-paths') },
    { label: group?.learningPath?.title || pathId, onClick: () => go(`/teacher-dashboard/my-students/learning-paths/${pathId}`) },
    { label: group?.name || t('myStudents.groups') }
  ] : [
    { label: t('myStudents.title'), onClick: () => go('/teacher-dashboard/my-students') },
    { label: t('myStudents.courseStudents'), onClick: () => go('/teacher-dashboard/my-students/courses') },
    { label: group?.course?.title || courseId, onClick: () => go(`/teacher-dashboard/my-students/courses/${courseId}`) },
    { label: group?.name || t('myStudents.groups') }
  ];
  const studentHref = (student) => pathId
    ? `/teacher-dashboard/my-students/learning-paths/${pathId}/groups/${groupId}/students/${student.id}`
    : `/teacher-dashboard/my-students/courses/${courseId}/groups/${groupId}/students/${student.id}`;
  return (
    <PageShell>
      <Breadcrumbs items={crumbs} />
      {group && (
        <>
          <div className="teacher-content-header">
            <h1 className="teacher-content-title">{group.name}</h1>
            <p className="teacher-content-subtitle">{group.contextLabel || group.learningPath?.title || group.course?.title}</p>
          </div>
          <div className="teacher-overview-grid">
            <OverviewCard title={t('myStudents.students')} value={group.studentCount} icon="people" />
            <OverviewCard title={t('myStudents.created')} value={formatDate(group.createdAt)} icon="event" variant="info" />
          </div>
        </>
      )}
      <div className="teacher-section">
        <div className="teacher-filters">
          <div className="teacher-search">
            <i className="material-icons">search</i>
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('myStudents.searchInGroup')} />
          </div>
          <div className="teacher-filter-group">
            <select className="teacher-select" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">{t('myStudents.allStatuses')}</option>
              <option value="active">{t('myStudents.statusActive')}</option>
              <option value="inactive">{t('myStudents.statusInactive')}</option>
              <option value="not_started">{t('myStudents.statusNotStarted')}</option>
            </select>
            <button type="button" className="teacher-btn primary" onClick={() => setShowStudent(true)}>
              <i className="material-icons">person_add</i>
              {t('myStudents.addStudent')}
            </button>
          </div>
        </div>
        {loading && <LoadingNote />}
        {error && <LoadError message={error} onRetry={load} />}
        {!loading && !error && data?.students?.length === 0 && (
          <div className="teacher-empty-state">
            <h3>{t('myStudents.noStudentsYet')}</h3>
            <div className="teacher-header-actions">
              <button type="button" className="teacher-btn primary" onClick={() => setShowStudent(true)}>
                <i className="material-icons">person_add</i>
                {t('myStudents.addStudent')}
              </button>
            </div>
          </div>
        )}
        {!loading && !error && data?.students?.length > 0 && (
          <div className="teacher-table-container">
            <table className="teacher-data-table">
              <thead>
                <tr>
                  <th>{t('myStudents.students')}</th>
                  <th>{t('myStudents.pathProgress')}</th>
                  <th>{t('myStudents.activity')}</th>
                  <th>{t('courses.table.status')}</th>
                </tr>
              </thead>
              <tbody>
                {data.students.map((s) => (
                  <tr key={s.id} className="teacher-row-click" onClick={() => go(studentHref(s))}>
                    <td>
                      <div className="student-cell">
                        <Avatar avatar={s.avatar} name={s.name} />
                        <div>
                          <strong>{s.name}</strong>
                          <div className="teacher-content-subtitle">{s.email}</div>
                        </div>
                      </div>
                    </td>
                    <td>{s.courseProgress ? `${s.courseProgress.percent}%` : `${s.completedCount || 0} ${t('myStudents.completed')}`}</td>
                    <td>{s.lastActivity ? formatDate(s.lastActivity) : '—'}</td>
                    <td><StatusBadge status={s.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {showStudent && (
        <AddStudentModal pathId={pathId} courseId={courseId} groups={group ? [group] : []} onClose={() => setShowStudent(false)} onAdded={() => { setShowStudent(false); load(); }} />
      )}
    </PageShell>
  );
}

export function StudentProfile({ go, studentId, groupId, pathId, courseId }) {
  const { t } = useTranslation('teacher');
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    teacherStudentsApi.profile(studentId, { groupId, pathId, courseId }).then((d) => setProfile(d.profile)).catch((err) => setError(err.message)).finally(() => setLoading(false));
  }, [studentId, groupId, pathId, courseId]);
  useEffect(() => { load(); }, [load]);
  const back = () => {
    if (pathId && groupId) go(`/teacher-dashboard/my-students/learning-paths/${pathId}/groups/${groupId}`);
    else if (courseId && groupId) go(`/teacher-dashboard/my-students/courses/${courseId}/groups/${groupId}`);
    else if (courseId) go(`/teacher-dashboard/my-students/courses/${courseId}`);
    else go('/teacher-dashboard/my-students');
  };
  return (
    <PageShell>
      <Breadcrumbs items={[
        { label: t('myStudents.title'), onClick: () => go('/teacher-dashboard/my-students') },
        { label: profile?.student?.name || t('myStudents.studentProfile') }
      ]} />
      {loading && <LoadingNote />}
      {error && <LoadError message={error} onRetry={load} />}
      {profile && (
        <>
          <div className="teacher-content-header">
            <div className="teacher-profile-row">
              <Avatar avatar={profile.student.avatar} name={profile.student.name} />
              <div>
                <h1 className="teacher-content-title">{profile.student.name}</h1>
                <p className="teacher-content-subtitle">
                  {[profile.learningPath?.title, profile.group?.name, profile.group?.contextLabel].filter(Boolean).join(' · ')}
                </p>
                <StatusBadge status={profile.status} />
              </div>
            </div>
          </div>
          {(profile.progress || profile.courseProgress) && (
            <div className="teacher-overview-grid">
              {profile.progress && (
                <>
                  <OverviewCard title={t('myStudents.pathProgress')} value={`${profile.progress.percent}%`} icon="trending_up" variant="success" />
                  <OverviewCard title={t('myStudents.completedContent')} value={`${profile.progress.completed} / ${profile.progress.total}`} icon="check_circle" />
                  {profile.progress.articlesInRetention != null && (
                    <OverviewCard title={t('myStudents.articlesInRetention')} value={profile.progress.articlesInRetention} icon="bookmark" variant="info" />
                  )}
                </>
              )}
              {profile.courseProgress && (
                <OverviewCard title={t('myStudents.courseProgress')} value={`${profile.courseProgress.percent}%`} icon="menu_book" variant="accent" />
              )}
            </div>
          )}
          {profile.activity && (
            <div className="teacher-section">
              <div className="teacher-section-header">
                <div className="teacher-section-title">
                  <i className="material-icons">insights</i>
                  {t('myStudents.activity')}
                </div>
              </div>
              <div className="teacher-overview-grid">
                {profile.activity.articlesCompleted != null && (
                  <OverviewCard title={t('myStudents.articlesCompleted')} value={profile.activity.articlesCompleted} icon="article" />
                )}
                {profile.activity.vocabularyInteractions != null && (
                  <OverviewCard title={t('myStudents.vocabularyActivity')} value={profile.activity.vocabularyInteractions} icon="spellcheck" variant="accent" />
                )}
                {profile.activity.dictationAttempts != null && (
                  <OverviewCard title={t('myStudents.dictationAttempts')} value={profile.activity.dictationAttempts} icon="mic" variant="info" />
                )}
                {profile.activity.dictationAccuracy != null && (
                  <OverviewCard title={t('myStudents.dictationAccuracy')} value={`${profile.activity.dictationAccuracy}%`} icon="grade" variant="success" />
                )}
              </div>
            </div>
          )}
          {profile.recent?.length > 0 && (
            <div className="teacher-section">
              <div className="teacher-section-header">
                <div className="teacher-section-title">
                  <i className="material-icons">history</i>
                  {t('myStudents.recentlyLearned')}
                </div>
              </div>
              <div className="teacher-table-container">
                <table className="teacher-data-table">
                  <thead>
                    <tr>
                      <th>{t('courses.table.title')}</th>
                      <th>{t('courses.table.lastUpdated')}</th>
                      <th>{t('courses.table.status')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {profile.recent.map((item) => (
                      <tr key={`${item.id}-${item.date}`}>
                        <td><strong>{item.title}</strong></td>
                        <td>{formatDate(item.date)}</td>
                        <td>
                          <span className={`status-badge ${item.completed ? 'completed' : 'pending'}`}>
                            {item.completed ? t('myStudents.completed') : item.accuracy != null ? `${item.accuracy}%` : t('myStudents.inProgress')}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {profile.needsAttention?.length > 0 && (
            <div className="teacher-section">
              <div className="teacher-section-header">
                <div className="teacher-section-title">
                  <i className="material-icons">warning</i>
                  {t('myStudents.needsAttention')}
                </div>
              </div>
              {profile.needsAttention.map((n) => (
                <p key={n.code} className="teacher-section-desc">{n.message}</p>
              ))}
            </div>
          )}
          <button type="button" className="teacher-btn secondary" onClick={back}>
            <i className="material-icons">arrow_back</i>
            {t('common.back')}
          </button>
        </>
      )}
    </PageShell>
  );
}

export function CourseList({ go }) {
  const { t } = useTranslation('teacher');
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    teacherStudentsApi.courses().then((d) => setCourses(d.courses || [])).catch((err) => setError(err.message)).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <PageShell>
      <Breadcrumbs items={[
        { label: t('myStudents.title'), onClick: () => go('/teacher-dashboard/my-students') },
        { label: t('myStudents.courseStudents') }
      ]} />
      <div className="teacher-content-header">
        <h1 className="teacher-content-title">{t('myStudents.courseStudents')}</h1>
      </div>
      {loading && <LoadingNote />}
      {error && <LoadError message={error} onRetry={load} />}
      {!loading && !error && courses.length === 0 && (
        <EmptyState title={t('myStudents.noCoursesYet')} hint={t('myStudents.noCoursesHint')} />
      )}
      {!loading && !error && courses.length > 0 && (
        <div className="teacher-section">
          <div className="teacher-actions-grid">
            {courses.map((c) => (
              <button
                type="button"
                key={c.id}
                className="teacher-action-card"
                onClick={() => go(`/teacher-dashboard/my-students/courses/${c.id}`)}
              >
                <div className="action-icon">
                  <i className="material-icons">menu_book</i>
                </div>
                <div className="action-content">
                  <h4>{c.title}</h4>
                  <p>{c.studentCount} {t('myStudents.students')} · {c.groupCount} {t('myStudents.groups')}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </PageShell>
  );
}

export function CourseManage({ go, courseId }) {
  const { t } = useTranslation('teacher');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showGroup, setShowGroup] = useState(false);
  const [showStudent, setShowStudent] = useState(false);
  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    teacherStudentsApi.course(courseId).then(setData).catch((err) => setError(err.message)).finally(() => setLoading(false));
  }, [courseId]);
  useEffect(() => { load(); }, [load]);
  return (
    <PageShell>
      <Breadcrumbs items={[
        { label: t('myStudents.title'), onClick: () => go('/teacher-dashboard/my-students') },
        { label: t('myStudents.courseStudents'), onClick: () => go('/teacher-dashboard/my-students/courses') },
        { label: data?.course?.title || t('myStudents.courseStudents') }
      ]} />
      <div className="teacher-content-header">
        <h1 className="teacher-content-title">{data?.course?.title || t('myStudents.courseStudents')}</h1>
      </div>
      <div className="teacher-section">
        <div className="teacher-section-header">
          <div className="teacher-section-title">
            <i className="material-icons">groups</i>
            {t('myStudents.groups')}
          </div>
          <div className="teacher-header-actions">
            <button type="button" className="teacher-btn secondary" onClick={() => setShowStudent(true)}>
              <i className="material-icons">person_add</i>
              {t('myStudents.addStudent')}
            </button>
            <button type="button" className="teacher-btn primary" onClick={() => setShowGroup(true)}>
              <i className="material-icons">group_add</i>
              {t('myStudents.createGroup')}
            </button>
          </div>
        </div>
        {loading && <LoadingNote />}
        {error && <LoadError message={error} onRetry={load} />}
        {!loading && !error && (data?.groups || []).length === 0 && (data?.students || []).length === 0 && (
          <div className="teacher-empty-state">
            <h3>{t('myStudents.noStudentsOrGroups')}</h3>
          </div>
        )}
        {(data?.groups || []).length > 0 && (
          <div className="teacher-actions-grid">
            {data.groups.map((g) => (
              <button
                type="button"
                key={g.id}
                className="teacher-action-card"
                onClick={() => go(`/teacher-dashboard/my-students/courses/${courseId}/groups/${g.id}`)}
              >
                <div className="action-icon">
                  <i className="material-icons">groups</i>
                </div>
                <div className="action-content">
                  <h4>{g.name}</h4>
                  <p>{g.studentCount} {t('myStudents.students')}</p>
                </div>
              </button>
            ))}
          </div>
        )}
        {(data?.students || []).length > 0 && (
          <div className="teacher-table-container" style={{ marginTop: 20 }}>
            <table className="teacher-data-table">
              <thead>
                <tr>
                  <th>{t('myStudents.students')}</th>
                  <th>{t('myStudents.courseProgress')}</th>
                  <th>{t('courses.table.status')}</th>
                </tr>
              </thead>
              <tbody>
                {data.students.map((s) => (
                  <tr
                    key={s.id}
                    className="teacher-row-click"
                    onClick={() => go(`/teacher-dashboard/my-students/courses/${courseId}/students/${s.id}`)}
                  >
                    <td>
                      <div className="student-cell">
                        <Avatar avatar={s.avatar} name={s.name} />
                        <div>
                          <strong>{s.name}</strong>
                          <div className="teacher-content-subtitle">{s.email}</div>
                        </div>
                      </div>
                    </td>
                    <td>{s.courseProgress ? `${s.courseProgress.percent}%` : '—'}</td>
                    <td><StatusBadge status={s.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {showGroup && <GroupModal courseId={courseId} onClose={() => setShowGroup(false)} onCreated={() => { setShowGroup(false); load(); }} />}
      {showStudent && <AddStudentModal courseId={courseId} groups={data?.groups || []} onClose={() => setShowStudent(false)} onAdded={() => { setShowStudent(false); load(); }} />}
    </PageShell>
  );
}
